import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { employeeService } from '../services/employeeService';
import '../styles/Dashboard.css';

const Dashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await employeeService.getAllEmployees();
      // Sort employees by creation date (most recent first)
      const sortedEmployees = (response.data.employees || []).sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA; // Most recent first
      });
      setEmployees(sortedEmployees);
      // Show only the last 10 onboarded by default
      setFilteredEmployees(sortedEmployees.slice(0, 10));
      setError(null);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setShowAll(false);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = employees.filter(emp => 
        emp.firstName?.toLowerCase().includes(query) ||
        emp.lastName?.toLowerCase().includes(query) ||
        emp.email?.toLowerCase().includes(query) ||
        emp.username?.toLowerCase().includes(query) ||
        emp.employeeId?.toLowerCase().includes(query)
      );
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees(employees.slice(0, 10));
    }
  };

  const handleCategoryFilter = (categoryId) => {
    setActiveCategory(categoryId);
    setShowAll(false);
    let filtered = employees;

    if (categoryId) {
      const categoryMap = {
        'hr': 'HR',
        'it': 'IT',
        'facility': 'FACILITY',
        'recruitment': 'RECRUITMENT',
      };
      const department = categoryMap[categoryId];
      filtered = employees.filter(emp => {
        // Handle both string and object department formats
        const deptName = typeof emp.department === 'string' ? emp.department : emp.department?.name;
        return deptName?.toUpperCase() === department;
      });
    }

    setFilteredEmployees(filtered);
    setSearchQuery('');
  };

  const categories = [
    { id: 'hr', name: 'HR', icon: '👤', color: '#ff6b6b' },
    { id: 'it', name: 'Local IT Support', icon: '💻', color: '#4ecdc4' },
    { id: 'facility', name: 'Facility', icon: '🏢', color: '#45b7d1' },
    { id: 'recruitment', name: 'Recruitment', icon: '📋', color: '#96ceb4' },
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Employee Directory</h1>
          <p className="subtitle">Welcome, {user?.firstName}!</p>
        </div>
        <div className="header-buttons">
          {user?.role === 'ADMIN' && (
            <Link to="/admin" className="admin-btn">
              🔐 Admin Panel
            </Link>
          )}
          <button onClick={handleLogout} className="logout-btn">
            Logout
          </button>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search by name, email, username, or employee ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">
            🔍 Search
          </button>
        </form>
      </div>

      {/* Category Filter Buttons */}
      <div className="categories-section">
        <h3>Filter by Department</h3>
        <div className="categories-grid">
          <button
            className={`category-btn ${activeCategory === null ? 'active' : ''}`}
            onClick={() => handleCategoryFilter(null)}
            style={{
              backgroundColor: activeCategory === null ? '#666' : 'transparent',
              color: activeCategory === null ? 'white' : '#333',
            }}
          >
            All Departments
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              className={`category-btn ${activeCategory === category.id ? 'active' : ''}`}
              style={{
                borderColor: category.color,
                backgroundColor: activeCategory === category.id ? category.color : 'transparent',
                color: activeCategory === category.id ? 'white' : '#333',
              }}
              onClick={() => handleCategoryFilter(category.id)}
            >
              <span className="category-icon">{category.icon}</span>
              <span className="category-name">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Employee Directory */}
      <div className="employees-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>
            {activeCategory ? categories.find(c => c.id === activeCategory)?.name + ' Team' : 'Recently Onboarded Employees'} 
            ({filteredEmployees.length})
          </h3>
          {!activeCategory && !searchQuery && (
            <button 
              onClick={() => {
                setShowAll(!showAll);
                setFilteredEmployees(showAll ? employees.slice(0, 10) : employees);
              }}
              style={{
                padding: '8px 16px',
                background: showAll ? '#ff6b6b' : '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {showAll ? 'Show Recent Only' : 'Show All Employees'}
            </button>
          )}
        </div>
        
        {loading ? (
          <div className="loading">Loading employees...</div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : filteredEmployees.length > 0 ? (
          <div className="employees-grid">
            {filteredEmployees.map((emp) => (
              <div key={emp.id} className="employee-card">
                <div className="employee-header">
                  <div className="employee-avatar">
                    {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                  </div>
                  <div className="employee-name-role">
                    <h4>{emp.firstName} {emp.lastName}</h4>
                    <p className="role-badge" style={{color: '#666'}}>{emp.role}</p>
                  </div>
                </div>

                <div className="employee-info">
                  <div className="info-row">
                    <span className="label">Employee ID:</span>
                    <span className="value">{emp.employeeId}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Full Name:</span>
                    <span className="value">{emp.firstName} {emp.lastName}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Email:</span>
                    <span className="value email-link">
                      <a href={`mailto:${emp.email}`}>{emp.email}</a>
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">Username:</span>
                    <span className="value">{emp.username}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Department:</span>
                    <span className="value">{(typeof emp.department === 'string' ? emp.department : emp.department?.name) || 'Not assigned'}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Role:</span>
                    <span className="value">{emp.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-results">
            <p>No employees found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Your Account Info */}
      <div className="user-info-section">
        <h3>Your Account Information</h3>
        <div className="info-card">
          <p>
            <strong>Employee ID:</strong> {user?.employeeId || 'N/A'}
          </p>
          <p>
            <strong>Full Name:</strong> {user?.firstName} {user?.lastName}
          </p>
          <p>
            <strong>Email:</strong> {user?.email || 'N/A'}
          </p>
          <p>
            <strong>Username:</strong> {user?.username}
          </p>
          <p>
            <strong>Department:</strong> {(typeof user?.department === 'string' ? user?.department : user?.department?.name) || 'Not assigned'}
          </p>
          <p>
            <strong>Role:</strong> {user?.role}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
